import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceStatusPanel = ({ project }) => {
  const complianceItems = [
    {
      category: 'Estándar Verra VCS',
      items: [
        {
          requirement: 'Registro del Proyecto',
          status: 'cumplido',
          lastUpdate: '2024-03-15',
          notes: 'Proyecto registrado bajo VCS v4.5'
        },
        {
          requirement: 'Validación Inicial',
          status: 'cumplido',
          lastUpdate: '2024-06-20',
          notes: 'Validación completada por auditor acreditado'
        },
        {
          requirement: 'Monitoreo Anual',
          status: 'en_progreso',
          lastUpdate: '2026-01-15',
          notes: 'Recolección de datos en curso'
        },
        {
          requirement: 'Verificación Periódica',
          status: 'programado',
          lastUpdate: '2026-02-28',
          notes: 'Próxima verificación programada'
        }
      ]
    },
    {
      category: 'Artículo 6 del Acuerdo de París',
      items: [
        {
          requirement: 'Autorización Nacional',
          status: 'cumplido',
          lastUpdate: '2024-04-10',
          notes: 'Autorización emitida por la DNA de México'
        },
        {
          requirement: 'Ajustes Correspondientes',
          status: 'cumplido',
          lastUpdate: '2025-01-20',
          notes: 'Ajustes aplicados según metodología aprobada'
        },
        {
          requirement: 'Reporte de Transferencias',
          status: 'en_progreso',
          lastUpdate: '2026-01-10',
          notes: 'Preparación de reporte para UNFCCC'
        }
      ]
    },
    {
      category: 'Salvaguardas Ambientales y Sociales',
      items: [
        {
          requirement: 'Evaluación de Impacto Ambiental',
          status: 'cumplido',
          lastUpdate: '2024-02-28',
          notes: 'EIA aprobada por autoridad competente'
        },
        {
          requirement: 'Consulta Comunitaria',
          status: 'cumplido',
          lastUpdate: '2024-03-05',
          notes: 'Consentimiento libre, previo e informado obtenido'
        },
        {
          requirement: 'Plan de Distribución de Beneficios',
          status: 'cumplido',
          lastUpdate: '2024-05-15',
          notes: 'Plan acordado con comunidades locales'
        },
        {
          requirement: 'Monitoreo de Biodiversidad',
          status: 'en_progreso',
          lastUpdate: '2025-12-10',
          notes: 'Monitoreo continuo de indicadores clave'
        }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'cumplido':
        return 'var(--color-success)';
      case 'en_progreso':
        return 'var(--color-warning)';
      case 'programado':
        return 'var(--color-primary)';
      case 'pendiente':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'cumplido':
        return 'CheckCircle';
      case 'en_progreso':
        return 'Clock';
      case 'programado':
        return 'Calendar';
      case 'pendiente':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      cumplido: 'Cumplido',
      en_progreso: 'En Progreso',
      programado: 'Programado',
      pendiente: 'Pendiente'
    };
    return labels?.[status] || status;
  };

  const calculateCategoryProgress = (items) => {
    const completed = items?.filter((item) => item?.status === 'cumplido')?.length;
    return Math.round((completed / items?.length) * 100);
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="Scale" size={20} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl font-semibold font-mono">Estado de Cumplimiento Regulatorio</h2>
      </div>
      <div className="space-y-6">
        {complianceItems?.map((category, categoryIndex) => {
          const progress = calculateCategoryProgress(category?.items);

          return (
            <div key={categoryIndex} className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-foreground">{category?.category}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground caption data-text">
                    {progress}%
                  </span>
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {category?.items?.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="p-3 bg-card rounded-md border border-border hover:border-primary/20 transition-all duration-250"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="flex items-center justify-center w-8 h-8 rounded-md flex-shrink-0"
                        style={{ backgroundColor: `${getStatusColor(item?.status)}15` }}
                      >
                        <Icon
                          name={getStatusIcon(item?.status)}
                          size={16}
                          color={getStatusColor(item?.status)}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-sm font-medium text-foreground">
                            {item?.requirement}
                          </h4>
                          <span
                            className="px-2 py-1 text-xs rounded caption font-medium flex-shrink-0"
                            style={{
                              backgroundColor: `${getStatusColor(item?.status)}15`,
                              color: getStatusColor(item?.status)
                            }}
                          >
                            {getStatusLabel(item?.status)}
                          </span>
                        </div>

                        <p className="text-xs text-muted-foreground caption mb-2">{item?.notes}</p>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground caption">
                          <Icon name="Calendar" size={12} />
                          <span>
                            Última actualización:{' '}
                            {new Date(item.lastUpdate)?.toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              Cumplimiento Regulatorio General
            </h4>
            <p className="text-xs text-muted-foreground caption">
              El proyecto mantiene un alto nivel de cumplimiento con los estándares internacionales
              de carbono y las regulaciones del Artículo 6 del Acuerdo de París. Todas las
              salvaguardas ambientales y sociales están siendo monitoreadas activamente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStatusPanel;